import { useState } from "react";
import { Search, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SUGGESTIONS = [
  "Best BBQ & restaurants",
  "Wineries & tasting rooms",
  "River tubing spots",
  "Things to do with kids",
  "Live music this weekend",
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const LocalSearch = () => {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (query) => {
    const term = (query ?? q).trim();
    if (term.length < 2) return toast.error("Type what you're looking for");
    setQ(term);
    setLoading(true);
    try {
      const { data } = await api.post("/local-search", { query: term });
      setRes(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Local search is unavailable right now");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="local-search" className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm lg:p-8">
      <div className="flex items-center gap-2 text-camel"><Sparkles size={18} /><span className="overline">Ask about the area</span></div>
      <h3 className="mt-2 text-2xl text-ink">Find restaurants, wineries &amp; things to do</h3>
      <p className="mt-2 text-sm text-taupe">Live local recommendations across the Texas Hill Country, powered by AI web search.</p>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input data-testid="local-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. best patios near Canyon Lake" className="h-12 rounded-full border-black/10 px-5" />
        <Button data-testid="local-search-btn" type="submit" disabled={loading} className="h-12 shrink-0 rounded-full btn-ink px-6">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <><Search size={16} className="mr-1.5" /> Search</>}
        </Button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2">
        {SUGGESTIONS.map((s) => (
          <button key={s} type="button" data-testid={`local-suggestion-${slug(s)}`} onClick={() => run(s)}
            className="rounded-full border border-black/10 px-3 py-1.5 text-xs text-ink/70 transition-colors hover:border-camel hover:text-camel">
            {s}
          </button>
        ))}
      </div>

      {res && (
        <div className="mt-6" data-testid="local-search-results">
          {res.answer && (
            <div className="rounded-2xl bg-cream p-4 text-sm leading-relaxed text-ink/90" data-testid="local-search-answer">
              {res.answer}
            </div>
          )}
          <div className="mt-4 space-y-3">
            {res.results.map((r) => (
              <a key={r.url} href={r.url} target="_blank" rel="noreferrer"
                className="block rounded-2xl border border-black/5 p-4 transition-colors hover:border-camel/40 hover:bg-cream/40">
                <div className="flex items-center gap-1.5 font-medium text-ink">{r.title} <ExternalLink size={13} className="text-taupe" /></div>
                <p className="mt-1 line-clamp-2 text-sm text-taupe">{r.content}</p>
              </a>
            ))}
          </div>
          {res.results.length === 0 && !res.answer && (
            <p className="text-sm text-taupe">No results found — try rephrasing your search.</p>
          )}
          <p className="mt-3 text-xs text-taupe">AI-summarized from the web — please verify hours &amp; availability with each spot.</p>
        </div>
      )}
    </div>
  );
};
